import { useState, useEffect, useContext, useRef } from 'react'
import moment from 'moment'
import { isFeatureEnabled, timeDisplayFormat, dateOnlyDisplayFormat, CLIENT_SPECIFIC_TEMPLATES_FOLDER_ID } from '../../utils'
import CustomTooltip from './CustomTooltip'
import { Pen, MoveToFolder, Delete } from '../../assets/icons'
import { ClickAwayListener } from '@material-ui/core'
import { useFoldersActions, useTemplatesActions } from '../../hooks'
import { CreateEditFolderModal, FoldersPopup } from '../misc'
import { Alert } from '.'
import ActionsDropdown from '../UI/ActionsDropdown'
import { DocumentsContext, FolderContext, NotificationContext, UserContext } from '../../context'
import { Folder, MoreVert, CreateNewFolder } from '@mui/icons-material'


const TemplatesFoldersTableRow = ({ id, folder, onSelectFolder, view = 'templates' }) => {
  const [createdAt, setCreatedAt] = useState('')
  const [createdAtTime, setCreatedAtTime] = useState('')
  const [modifiedAt, setModifiedAt] = useState('')
  const [modifiedAtTime, setModifiedAtTime] = useState('')
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false)
  const [showEditFolderModal, setShowEditFolderModal] = useState(false)
  const [showFoldersPopup, setShowFoldersPopup] = useState(false)
  const { deleteFolder, checkIfFolderCanBeDeleted } = useFoldersActions()
  const [showDeleteAlert, setShowDeleteAlert] = useState(false)
  const popupEl = useRef()
  const { folders, foldersLoading, standardTemplatesFolders, standardTemplatesFoldersLoading } = useContext(FolderContext)
  const { templates, standardTemplates } = useContext(DocumentsContext)
  const { user, integration } = useContext(UserContext)
  const [popupElPosition, setPopupElPosition] = useState(null)
  // const [folder, setFolder] = useState(false)
  const { moveTemplateToFolder } = useTemplatesActions()
  const [showDropdown, setShowDropdown] = useState(false);
  const { setNotification } = useContext(NotificationContext)

  // Set metadata
  useEffect(() => {
    // setFolder(template)
    if (folder?.meta?.created) {
      setCreatedAt(moment(folder.meta.created).format(dateOnlyDisplayFormat))
      setCreatedAtTime(moment(folder.meta.created).format(timeDisplayFormat))
    } else if (folder.createdAt) {
      setCreatedAt(moment(folder.createdAt).format(dateOnlyDisplayFormat))
      setCreatedAtTime(moment(folder.createdAt).format(timeDisplayFormat))
    }
    if (folder?.meta?.updated) {
      setModifiedAt(moment(folder.meta.updated).format(dateOnlyDisplayFormat))
      setModifiedAtTime(moment(folder.meta.updated).format(timeDisplayFormat))
    } else if (folder.updatedAt) {
      setModifiedAt(moment(folder.updatedAt).format(dateOnlyDisplayFormat))
      setModifiedAtTime(moment(folder.updatedAt).format(timeDisplayFormat))
    }
  }, [folder])


  // On click away
  const handleClickAway = () => {
    setShowFoldersPopup(false)
  }

  // On create new folder click
  const handleCreateNewFolderClick = (e) => {
    e.preventDefault()
    setShowCreateFolderModal(true)
  }

  // On edit click
  const handleEditFolderClick = (e) => {
    e.preventDefault()
    setShowEditFolderModal(true)
  }

  // On move folder click
  const handleMoveFolderClick = (e) => {
    e.preventDefault()
    setShowFoldersPopup(true)
    setPopupElPosition(e.currentTarget.getBoundingClientRect())
  }

  // On delete folder click
  const handleDeleteFolderClick = (e) => {
    e.preventDefault()
    if (checkIfFolderCanBeDeleted(folder, view)) {
      setShowDeleteAlert(true)
    }
  }

  // On close delete alert
  const handleCloseDeleteAlert = () => {
    setShowDeleteAlert(false)
  }

  // On delete 
  const handleDelete = async () => {
    setShowDeleteAlert(false)
    await deleteFolder(folder, view)
    // if (activeFolder === folder.id) {
    //   onFilter(folder.parentFolder || 'all')
    //   return
    // }
  }


  // On close edit folder modal
  const handleCloseEditFolderModal = () => {
    setShowEditFolderModal(false)
  }

  // On close create folder modal
  const handleCloseCreateFolderModal = () => {
    setShowCreateFolderModal(false)
  }

  // On close folders popup
  const handleCloseFoldersPopup = () => {
    setShowFoldersPopup(false)
  }

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // drop template to folder
  const handleDrop = (e) => {
    const template = e.dataTransfer.getData('template');
    const view = e.dataTransfer.getData('view');
    const feature = `folder-actions-${view.replace(/_/g, '-')}`;
    if(!isFeatureEnabled(feature, integration) || folder?.id === CLIENT_SPECIFIC_TEMPLATES_FOLDER_ID) {
      setNotification({ msg: 'Opération non autorisée', type: 'danger' });
      return
    };
    moveTemplateToFolder({ folder, template: JSON.parse(template), view: view });
  };

  return (<>
    <div className={`documents-table-row !pl-0 no-cursor`}
      onDragOver={handleDragOver}
      onDrop={handleDrop}>
      <div className="documents-table-column !pl-0">
        <div className="flex gap-2 px-3">
          <div onClick={() => onSelectFolder(folder)} className='cursor-pointer'><Folder /></div>
          {isFeatureEnabled(`folder-actions-${view.replace(/_/g, '-')}`, integration) && folder?.id !== CLIENT_SPECIFIC_TEMPLATES_FOLDER_ID &&
            <ActionsDropdown
              trigger={<button className="icon-btn icon-btn--transparent icon-btn--svg-xl more-vert-icon" onClick={(e) => { e.preventDefault(); setShowDropdown(!showDropdown) }}><span className="svg-icon"><MoreVert fontSize='medium' /></span></button>}
              hideHeader
              width={120}
              dropdownClass="actions-dropdown--no-padding"
            >
              <ul className="dashboard-column__dropdown">
                <li onClick={handleCreateNewFolderClick} className="edit"><span className="icon"><CreateNewFolder /></span>Nouveau dossier</li>
                <li onClick={handleEditFolderClick} className="edit"><span className="icon"><Pen /></span>Renommer</li>
                <li onClick={handleMoveFolderClick} className="edit"><span className="icon"><MoveToFolder /></span>Déplacer</li>
                <li onClick={handleDeleteFolderClick}><span className="icon"><Delete /></span>Supprimer</li>
              </ul>
            </ActionsDropdown>
          }
        </div>

        <p className='doc_name cursor-pointer line-clamp-2'>
          <span onClick={() => onSelectFolder(folder)}>{folder.name}</span>
        </p>
      </div>
      {view === 'templates' && <div className="documents-table-column documents-table-column__status">
      </div>}
      <div className="documents-table-column documents-table-column__date line-clamp-2">
        <div className="w-full">
          <p className='date'>{createdAt}</p>
          <p className='time'>{createdAtTime}</p>
        </div>
      </div>
      <div className="documents-table-column documents-table-column__date">
        <div className="w-full">
          <p className='date'>{modifiedAt}</p>
          <p className='time'>{modifiedAtTime}</p>
        </div>
      </div>
      {view === 'templates' && user.validation_access === 'manager' && <div className="documents-table-column documents-table-column__validation"></div>}
      {view === 'templates' && <div className="documents-table-column documents-table-column__history"></div>}

    </div>

    {showFoldersPopup && <ClickAwayListener onClickAway={handleClickAway}>
      <div>
        <FoldersPopup
          ref={popupEl}
          onCancel={handleCloseFoldersPopup}
          folders={view === 'templates' ? folders : view === 'standard-templates' ? standardTemplatesFolders : []}
          foldersLoading={view === 'templates' ? foldersLoading : view === 'standard-templates' ? standardTemplatesFoldersLoading : false}
          loading={false}
          data={view === 'templates' ? templates : view === 'standard-templates' ? standardTemplates : []}
          resource={folder}
          position={popupElPosition}
          view={view}
        />

      </div>
    </ClickAwayListener>}
    {showCreateFolderModal && (
      <CreateEditFolderModal
        onClose={handleCloseCreateFolderModal}
        current={folder}
        view={view}
      />
    )}

    {showEditFolderModal && (
      <CreateEditFolderModal
        onClose={handleCloseEditFolderModal}
        current={folder}
        mode="edit"
        view={view}
      />
    )}

    {showDeleteAlert && (
      <Alert
        onClose={handleCloseDeleteAlert}
        onSubmit={handleDelete}
        deleteAlert={true}
        text="Êtes-vous sûr de vouloir supprimer ce dossier ?"
      />
    )}

  </>

  )
}

export default TemplatesFoldersTableRow